/**
 * Photos into the `Cx Evidence` library.
 *
 * Two things carry the binding between a photo and what it evidences, and both
 * are set at capture:
 *
 *  - **the folder** — `<ProjectCode>/<AssetTag>/<ShotType>/`
 *  - **the filename** — `<AssetTag>__<ShotType>__<ISO timestamp>.jpg`
 *
 * Belt and braces on purpose. If the list row is ever lost, the library is still
 * a sorted evidence pack; if the folder structure is ever flattened, every file
 * still says what it is. The one thing that must never happen is a photo landing
 * somewhere that does not know its breaker and shot, because nothing downstream
 * can work that out again.
 *
 * SharePoint will not create intermediate folders for you: uploading to a path
 * whose parent does not exist fails with a 404 that reads like a permissions
 * problem. So each level is ensured, in order, and remembered.
 */

import { SpError, type SpHttp } from '../api/http.js';
import { odataString } from '../api/http.js';

export interface EvidenceRef {
  /** Server-relative path of the uploaded file. */
  serverRelativeUrl: string;
  /** Absolute URL, for a result line's PhotoUrl. */
  url: string;
  name: string;
}

export interface EvidenceTarget {
  projectCode: string;
  assetTag: string;
  shotCode: string;
  capturedAt?: Date;
}

/**
 * A path segment SharePoint will accept.
 *
 * Asset tags contain `/` by design — `…M3A1-AU/AU2-K1` — which is a folder
 * separator, not a character. Left alone it would silently nest every breaker
 * under its board's name and split one lineup across 18 folders.
 */
export function safeSegment(value: string): string {
  return value
    .replace(/[/\\]/g, '_')
    .replace(/["#%*:<>?|~]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+|\.+$/g, '')
    .slice(0, 120);
}

export function evidenceFolder(libraryPath: string, t: EvidenceTarget): string {
  return [libraryPath.replace(/\/+$/, ''), safeSegment(t.projectCode), safeSegment(t.assetTag), safeSegment(t.shotCode)]
    .join('/');
}

export function evidenceFileName(t: EvidenceTarget): string {
  const when = (t.capturedAt ?? new Date()).toISOString().replace(/[:.]/g, '-');
  return `${safeSegment(t.assetTag)}__${safeSegment(t.shotCode)}__${when}.jpg`;
}

/**
 * Creates the folder chain, one level at a time, skipping what is already there.
 *
 * The cache matters: a lineup uploads dozens of photos into the same few
 * folders, and asking SharePoint to create each level for every photo is the
 * difference between a sync that finishes on a site connection and one that
 * does not.
 */
export class EvidenceLibrary {
  private readonly known = new Set<string>();

  constructor(
    private readonly http: SpHttp,
    /** Server-relative path of the library, e.g. /sites/CxPlatform/Cx Evidence */
    private readonly libraryPath: string,
  ) {}

  async ensureFolder(path: string): Promise<void> {
    if (this.known.has(path)) return;

    const root = this.libraryPath.replace(/\/+$/, '');
    if (!path.startsWith(root)) {
      throw new SpError(0, path, `refusing to create a folder outside ${root}`);
    }

    const rest = path.slice(root.length).split('/').filter(Boolean);
    let current = root;
    for (const segment of rest) {
      current = `${current}/${segment}`;
      if (this.known.has(current)) continue;
      try {
        await this.http.get(`web/getfolderbyserverrelativeurl(${odataString(current)})?$select=Exists`);
      } catch {
        // Not there — make it. A failure here is a real failure and propagates.
        await this.http.create('web/folders', 'SP.Folder', { ServerRelativeUrl: current });
      }
      this.known.add(current);
    }
    this.known.add(path);
  }

  /** Uploads one photo and returns where it landed. */
  async upload(target: EvidenceTarget, blob: Blob): Promise<EvidenceRef> {
    if (blob.size === 0) {
      // An empty blob means the camera handed back nothing. Uploading it would
      // tick the slot green with no evidence behind it.
      throw new SpError(0, target.shotCode, 'refusing to upload an empty photo');
    }

    const folder = evidenceFolder(this.libraryPath, target);
    const name = evidenceFileName(target);
    await this.ensureFolder(folder);

    const path =
      `web/getfolderbyserverrelativeurl(${odataString(folder)})` +
      `/files/add(overwrite=true,url=${odataString(name)})`;
    const res = await this.http.uploadBinary<{ ServerRelativeUrl?: string; Name?: string }>(path, blob);

    const serverRelativeUrl = res.ServerRelativeUrl ?? `${folder}/${name}`;
    return { serverRelativeUrl, url: serverRelativeUrl, name: res.Name ?? name };
  }
}

/** What a photo needs to become a queued write once it is uploaded. */
export interface PhotoWrite {
  folder: string;
  fileName: string;
  target: EvidenceTarget;
}

/** Everything about where a photo goes, decided at capture and never re-derived. */
export function planPhoto(libraryPath: string, target: EvidenceTarget): PhotoWrite {
  return {
    folder: evidenceFolder(libraryPath, target),
    fileName: evidenceFileName(target),
    target,
  };
}
